<template>
  <v-container fluid>
    <!-- Header -->
    <v-row align="center" class="mb-3">
      <v-col cols="auto">
        <v-btn icon variant="text" @click="$router.back()">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
      </v-col>
      <v-col>
        <div class="text-h5 font-weight-bold">
          <v-icon class="mr-2">mdi-chart-timeline-variant</v-icon>
          Rapoarte Trafic
        </div>
      </v-col>
      <v-col cols="auto">
        <v-btn
          v-if="isAdmin"
          color="primary"
          variant="tonal"
          size="small"
          :loading="importing"
          @click="triggerImport"
        >
          <v-icon class="mr-1" size="small">mdi-download</v-icon>
          Import CDR
        </v-btn>
        <v-btn
          color="success"
          variant="tonal"
          size="small"
          class="ml-2"
          @click="exportReport"
        >
          <v-icon class="mr-1" size="small">mdi-file-export</v-icon>
          Export CSV
        </v-btn>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-card class="mb-4 pa-3">
      <v-row dense align="start">
        <v-col cols="12" sm="6" md="3">
          <ClientHierarchySelect
            v-model="filters.clientId"
            placeholder="Client"
            :auto-select-own="true"
            @change="onFilterClientChange"
            @clients-loaded="onClientsLoaded"
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-autocomplete
            v-model="filters.simId"
            :items="simsList"
            :item-title="simLabel"
            item-value="id"
            label="SIM (MSISDN / IMSI / Serial)"
            density="compact"
            variant="outlined"
            hide-details
            clearable
            :loading="loadingFilterSims"
            no-data-text="Cauta dupa MSISDN, IMSI sau serial (min 3 car.)..."
            :no-filter="true"
            @update:model-value="loadData"
            @update:search="onSimSearchInput"
          />
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="filters.imsiGroupId"
            :items="imsiGroupsList"
            item-title="name"
            item-value="id"
            label="Grup IMSI"
            density="compact"
            variant="outlined"
            hide-details
            clearable
            :loading="loadingImsiGroups"
            :disabled="!filters.clientId"
            no-data-text="Niciun grup"
            @update:model-value="onImsiGroupChange"
          />
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="filters.networkType"
            :items="networkTypes"
            label="Retea"
            density="compact"
            variant="outlined"
            hide-details
            @update:model-value="loadData"
          />
        </v-col>
      </v-row>
      <!-- Date Range Section -->
      <v-row dense align="center" class="mt-2">
        <v-col cols="6" sm="3" md="2">
          <v-text-field
            v-model="filters.startDate"
            label="De la"
            type="date"
            density="compact"
            variant="outlined"
            hide-details
          />
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-text-field
            v-model="filters.endDate"
            label="Pana la"
            type="date"
            density="compact"
            variant="outlined"
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="6" md="8">
          <div class="d-flex ga-1 flex-wrap align-center">
            <v-btn size="small" variant="tonal" :color="activePeriod === 'today' ? 'primary' : 'default'" @click="setPeriod('today')">Azi</v-btn>
            <v-btn size="small" variant="tonal" :color="activePeriod === 'yesterday' ? 'primary' : 'default'" @click="setPeriod('yesterday')">Ieri</v-btn>
            <v-btn size="small" variant="tonal" :color="activePeriod === 'last7' ? 'primary' : 'default'" @click="setPeriod('last7')">7 zile</v-btn>
            <v-btn size="small" variant="tonal" :color="activePeriod === 'last30' ? 'primary' : 'default'" @click="setPeriod('last30')">30 zile</v-btn>
            <v-btn size="small" variant="tonal" :color="activePeriod === 'currentMonth' ? 'primary' : 'default'" @click="setPeriod('currentMonth')">Luna curenta</v-btn>
            <v-btn size="small" variant="tonal" :color="activePeriod === 'lastMonth' ? 'primary' : 'default'" @click="setPeriod('lastMonth')">Luna trecuta</v-btn>
            <v-btn size="small" variant="tonal" :color="activePeriod === 'all' ? 'primary' : 'default'" @click="setPeriod('all')">Toate</v-btn>
            <v-divider vertical class="mx-1" />
            <v-btn color="primary" variant="flat" @click="loadData">
              <v-icon size="small" class="mr-1">mdi-magnify</v-icon>
              Cauta
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <!-- Loading -->
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />

    <!-- Usage Comparison (when a specific SIM is selected) -->
    <TrafficUsageComparison
      v-if="filters.simId"
      :simId="filters.simId"
      :token="token"
      period="current_month"
    />

    <!-- Stats Cards -->
    <TrafficSummaryCards :stats="stats" class="mb-4" />

    <!-- Chart -->
    <TrafficChart :summaries="summaries" class="mb-4" />

    <!-- Details Table -->
    <TrafficDetailsTable
      :records="records"
      :total-count="pagination.totalCount"
      :loading="loadingDetails"
      :page="pagination.page"
      :per-page="pagination.perPage"
      @update:page="onPageChange"
      @update:per-page="onPerPageChange"
      @update:record-type="onRecordTypeChange"
      @update:search="onSearchChange"
    />

    <!-- Import Dialog -->
    <v-dialog v-model="importDialog" max-width="550">
      <v-card>
        <v-card-title class="bg-primary text-white">
          <v-icon class="mr-2">mdi-download</v-icon>
          Import CDR de la MVNO
        </v-card-title>
        <v-card-text class="pt-4">
          <v-autocomplete
            v-model="importClientId"
            :items="clientsList"
            item-title="name"
            item-value="id"
            label="Client"
            density="compact"
            variant="outlined"
            clearable
            @update:model-value="onImportClientChange"
          />
          <v-autocomplete
            v-model="importSimId"
            :items="importSimsList"
            :item-title="importSimLabel"
            item-value="id"
            label="SIM (optional - toate SIM-urile clientului daca e gol)"
            density="compact"
            variant="outlined"
            clearable
            :loading="loadingImportSims"
            :disabled="!importClientId"
            no-data-text="Niciun SIM cu abonament MVNO gasit"
            @update:model-value="onImportSimChange"
          />
          <v-alert v-if="importClientId && !loadingImportSims && importSimsList.length === 0" type="info" density="compact" variant="tonal" class="mt-1">
            Niciun SIM provizionat la MVNO pentru acest client.
          </v-alert>
          <v-alert v-if="lastImportDate" type="info" density="compact" variant="tonal" class="mt-2 mb-2">
            Ultimul import: {{ lastImportDate }} ({{ lastImportTotalRecords }} inregistrari in DB)
          </v-alert>
          <v-row dense>
            <v-col cols="6">
              <v-text-field v-model="importFromDate" label="De la" type="date" density="compact" variant="outlined" />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="importToDate" label="Pana la" type="date" density="compact" variant="outlined" />
            </v-col>
          </v-row>
          <div class="d-flex ga-1 flex-wrap mb-2">
            <v-btn size="x-small" variant="tonal" @click="setImportPeriod('today')">Azi</v-btn>
            <v-btn size="x-small" variant="tonal" @click="setImportPeriod('yesterday')">Ieri</v-btn>
            <v-btn size="x-small" variant="tonal" @click="setImportPeriod('last7')">7 zile</v-btn>
            <v-btn size="x-small" variant="tonal" @click="setImportPeriod('last30')">30 zile</v-btn>
            <v-btn size="x-small" variant="tonal" @click="setImportPeriod('currentMonth')">Luna curenta</v-btn>
            <v-btn size="x-small" variant="tonal" @click="setImportPeriod('lastMonth')">Luna trecuta</v-btn>
            <v-btn v-if="lastImportDate" size="x-small" variant="tonal" color="primary" @click="setImportPeriod('sinceLastImport')">De la ultimul import</v-btn>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="importDialog = false">Anuleaza</v-btn>
          <v-btn color="primary" variant="flat" :loading="importing" :disabled="!importClientId" @click="executeImport">Import</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import TrafficSummaryCards from '@/components/traffic/TrafficSummaryCards.vue'
import TrafficChart from '@/components/traffic/TrafficChart.vue'
import TrafficDetailsTable from '@/components/traffic/TrafficDetailsTable.vue'
import TrafficUsageComparison from '@/components/traffic/TrafficUsageComparison.vue'
import ClientHierarchySelect from '@/components/order/ClientHierarchySelect.vue'
import { getTrafficSummary, getTrafficDetails, getTrafficStats, exportTrafficReport, importTrafficData, getTrafficLastImport } from '@/composables/useTrafficAPI'
import { getCache, setCache, getMemCache, setMemCache } from '@/composables/useLocalCache'

export default {
  name: 'TrafficReportsView',
  components: { TrafficSummaryCards, TrafficChart, TrafficDetailsTable, TrafficUsageComparison, ClientHierarchySelect },
  data() {
    const today = new Date().toISOString().split('T')[0]

    return {
      loading: false,
      loadingDetails: false,
      importing: false,
      stats: {},
      summaries: [],
      records: [],
      pagination: { page: 1, perPage: 50, totalCount: 0, totalPages: 0 },
      filters: {
        clientId: null,
        simId: null,
        imsiGroupId: null,
        startDate: today,
        endDate: today,
        networkType: 'all',
        recordType: null,
        search: null
      },
      networkTypes: [
        { title: 'Toate', value: 'all' },
        { title: 'National', value: 'national' },
        { title: 'Roaming', value: 'roaming' },
        { title: 'International', value: 'international' }
      ],
      activePeriod: 'today',
      loadingFilterSims: false,
      loadingImsiGroups: false,
      clientsList: [],
      simsList: [],
      imsiGroupsList: [],
      simSearchDebounce: null,
      importDialog: false,
      importFromDate: today,
      importToDate: today,
      importClientId: null,
      importSimId: null,
      importSimsList: [],
      loadingImportSims: false,
      lastImportDate: null,
      lastImportTotalRecords: 0,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success'
    }
  },
  computed: {
    token() { return this.$store.state.token },
    isAdmin() { return this.$store.state.user?.admin || this.$store.state.user?.is_admin || false },
    currentClientId() { return this.$store.state.user?.client_id }
  },
  async mounted() {
    // Set client from query param
    if (this.$route.query.client_id) {
      this.filters.clientId = parseInt(this.$route.query.client_id)
    }
    if (this.$route.query.sim_id) {
      this.filters.simId = parseInt(this.$route.query.sim_id)
    }

    // ClientHierarchySelect loads clients and auto-selects own client
    // Load SIMs only if a client is pre-selected (from query params)
    if (this.filters.clientId) {
      await this.loadSimsList()
    }
    await this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        await Promise.all([
          this.loadStats(),
          this.loadSummaries(),
          this.loadDetails()
        ])
      } finally {
        this.loading = false
      }
    },

    async loadStats() {
      const result = await getTrafficStats(this.token, {
        clientId: this.filters.clientId,
        period: 'custom',
        imsiGroupId: this.filters.imsiGroupId
      })
      if (!result.error) {
        this.stats = result.stats || {}
      }
    },

    async loadSummaries() {
      const result = await getTrafficSummary(this.token, {
        simId: this.filters.simId,
        clientId: this.filters.clientId,
        periodType: 'daily',
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        networkType: this.filters.networkType,
        imsiGroupId: this.filters.imsiGroupId
      })
      if (!result.error) {
        this.summaries = result.summaries || []
      }
    },

    async loadDetails() {
      this.loadingDetails = true
      try {
        const result = await getTrafficDetails(this.token, {
          simId: this.filters.simId,
          clientId: this.filters.clientId,
          recordType: this.filters.recordType,
          search: this.filters.search,
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
          page: this.pagination.page,
          perPage: this.pagination.perPage,
          imsiGroupId: this.filters.imsiGroupId
        })
        if (!result.error) {
          this.records = result.records || []
          if (result.pagination) {
            this.pagination.totalCount = result.pagination.total_count || 0
            this.pagination.totalPages = result.pagination.total_pages || 0
          }
        }
      } finally {
        this.loadingDetails = false
      }
    },

    onClientsLoaded(clientsList) {
      this.clientsList = clientsList
    },

    async onFilterClientChange({ clientId }) {
      this.filters.simId = null
      this.filters.imsiGroupId = null
      this.simsList = []
      this.imsiGroupsList = []
      if (clientId) {
        await this.loadImsiGroups()
      }
      this.loadData()
    },

    // Search SIMs: first in local cache, fallback to server
    filterSimsLocally(searchTerm) {
      const clientId = this.filters.clientId || '_all'
      const cacheKey = `sims_${clientId}`
      const cached = getMemCache(cacheKey)
      if (!cached || cached.length === 0) return null

      const s = searchTerm.toLowerCase()
      return cached.filter(sim => {
        const msisdn = (sim.msisdn || sim.msisdn_number || '').toLowerCase()
        const serial = (sim.serial || '').toLowerCase()
        const imsi = (sim.imsi_number || sim.imsi || '').toLowerCase()
        const subId = (sim.subscriber_id || '').toString().toLowerCase()
        return msisdn.includes(s) || serial.includes(s) || imsi.includes(s) || subId.includes(s)
      })
    },

    mergeSims(existing, newSims) {
      // Merge new SIMs into existing cache (by id), returns combined array
      const map = new Map()
      if (existing) existing.forEach(s => map.set(s.id, s))
      newSims.forEach(s => map.set(s.id, s))
      return Array.from(map.values())
    },

    async fetchSimsFromServer(searchTerm) {
      const { BASE_URL } = await import('@/composables/url_config')
      const axios = (await import('axios')).default
      const headers = {
        Authorization: `Bearer ${this.token}`,
        'Content-Type': 'application/json'
      }
      const params = new URLSearchParams()
      if (this.filters.clientId) params.append('client_id', this.filters.clientId)
      params.append('include_subclients', 'true')
      params.append('has_subscriber_id', 'true')
      params.append('sim_status_id', 'all')
      params.append('per_page', '50')
      if (searchTerm && searchTerm.length >= 3) params.append('search', searchTerm)
      const result = await axios.get(`${BASE_URL}/home/get_sims_list_api?${params}`, { headers })
      if (result.data && !result.data.error) {
        return result.data.sims || []
      }
      return []
    },

    async loadSimsList(searchTerm) {
      const clientId = this.filters.clientId || '_all'
      const cacheKey = `sims_${clientId}`

      // Try local cache first
      if (searchTerm && searchTerm.length >= 3) {
        const localResults = this.filterSimsLocally(searchTerm)
        if (localResults && localResults.length > 0) {
          this.simsList = localResults
          return
        }
      } else if (!searchTerm) {
        // No search - show cached items if available
        const cached = getMemCache(cacheKey)
        if (cached && cached.length > 0) {
          this.simsList = cached
          return
        }
      }

      // Fallback: fetch from server
      this.loadingFilterSims = true
      try {
        const serverSims = await this.fetchSimsFromServer(searchTerm)
        // Merge into cache progressively
        const existingCache = getMemCache(cacheKey) || []
        const merged = this.mergeSims(existingCache, serverSims)
        setMemCache(cacheKey, merged, 10) // 10 min TTL

        if (searchTerm && searchTerm.length >= 3) {
          this.simsList = serverSims
        } else {
          this.simsList = merged
        }
      } catch (e) {
        console.error('Error loading SIMs list:', e)
      } finally {
        this.loadingFilterSims = false
      }
    },

    async loadImsiGroups() {
      if (!this.filters.clientId) return

      // Check cache
      const cacheKey = `imsi_groups_${this.filters.clientId}`
      const cached = getCache(cacheKey)
      if (cached) {
        this.imsiGroupsList = cached
        return
      }

      this.loadingImsiGroups = true
      try {
        const { BASE_URL } = await import('@/composables/url_config')
        const axios = (await import('axios')).default
        const headers = {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
        const result = await axios.get(`${BASE_URL}/home/getImsiGroupsApi`, { headers })
        if (result.data && !result.data.error) {
          this.imsiGroupsList = result.data.groups || result.data.imsi_groups || []
          setCache(cacheKey, this.imsiGroupsList, 10)
        }
      } catch (e) {
        console.error('Error loading IMSI groups:', e)
      } finally {
        this.loadingImsiGroups = false
      }
    },

    async onImsiGroupChange(groupId) {
      this.filters.simId = null
      if (groupId) {
        await this.loadSimsFromGroup(groupId)
      } else if (this.filters.clientId) {
        await this.loadSimsList()
      }
      this.loadData()
    },

    async loadSimsFromGroup(groupId) {
      this.loadingFilterSims = true
      try {
        const { BASE_URL } = await import('@/composables/url_config')
        const axios = (await import('axios')).default
        const headers = {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
        const result = await axios.get(`${BASE_URL}/home/getImsiGroupMembersApi?imsi_group_id=${groupId}&per_page=500`, { headers })
        if (result.data && !result.data.error) {
          const members = result.data.members || []
          this.simsList = members.filter(m => m.sim_id).map(m => ({
            id: m.sim_id,
            msisdn_number: m.msisdn || '',
            serial: m.imsi_serial || m.serial || '',
            imsi: m.imsi || '',
            sim_status_name: m.sim_status_name || ''
          }))
        }
      } catch (e) {
        console.error('Error loading group members:', e)
      } finally {
        this.loadingFilterSims = false
      }
    },

    onSimSearchInput(searchText) {
      if (this.simSearchDebounce) clearTimeout(this.simSearchDebounce)
      if (!searchText || searchText.length < 3) {
        // Search cleared - show full cached list
        if (!searchText || searchText.length === 0) {
          this.loadSimsList()
        }
        return
      }
      // Try local cache first (instant)
      const localResults = this.filterSimsLocally(searchText)
      if (localResults && localResults.length > 0) {
        this.simsList = localResults
        return
      }
      // Not in cache - debounced server search
      this.simSearchDebounce = setTimeout(() => {
        this.loadSimsList(searchText)
      }, 300)
    },

    simLabel(sim) {
      const msisdn = sim.msisdn_number || sim.msisdn || ''
      const serial = sim.serial || sim.imsi_serial || ''
      const imsi = sim.imsi || sim.imsi_number || ''
      const status = sim.sim_status_name ? ` [${sim.sim_status_name}]` : ''
      const parts = [msisdn, serial, imsi].filter(Boolean)
      return parts.length > 0 ? `${parts.join(' / ')}${status}` : `SIM #${sim.id}${status}`
    },

    setPeriod(period) {
      this.activePeriod = period
      const today = new Date()
      const fmt = (d) => d.toISOString().split('T')[0]

      switch (period) {
        case 'today':
          this.filters.startDate = fmt(today)
          this.filters.endDate = fmt(today)
          break
        case 'yesterday': {
          const yesterday = new Date(today)
          yesterday.setDate(yesterday.getDate() - 1)
          this.filters.startDate = fmt(yesterday)
          this.filters.endDate = fmt(yesterday)
          break
        }
        case 'last7': {
          const d = new Date(today)
          d.setDate(d.getDate() - 7)
          this.filters.startDate = fmt(d)
          this.filters.endDate = fmt(today)
          break
        }
        case 'last30': {
          const d = new Date(today)
          d.setDate(d.getDate() - 30)
          this.filters.startDate = fmt(d)
          this.filters.endDate = fmt(today)
          break
        }
        case 'currentMonth': {
          const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
          this.filters.startDate = fmt(firstDay)
          this.filters.endDate = fmt(today)
          break
        }
        case 'lastMonth': {
          const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1)
          const lastDay = new Date(today.getFullYear(), today.getMonth(), 0)
          this.filters.startDate = fmt(firstDay)
          this.filters.endDate = fmt(lastDay)
          break
        }
        case 'all':
          this.filters.startDate = '2020-01-01'
          this.filters.endDate = fmt(today)
          break
      }
      // Auto-search if client or SIM is selected
      if (this.filters.clientId || this.filters.simId) {
        this.loadData()
      }
    },

    onPageChange(page) {
      this.pagination.page = page
      this.loadDetails()
    },

    onPerPageChange(perPage) {
      this.pagination.perPage = perPage
      this.pagination.page = 1
      this.loadDetails()
    },

    onRecordTypeChange(type) {
      this.filters.recordType = type
      this.pagination.page = 1
      this.loadDetails()
    },

    onSearchChange(search) {
      this.filters.search = search
      this.pagination.page = 1
      this.loadDetails()
    },

    async exportReport() {
      const result = await exportTrafficReport(this.token, {
        simId: this.filters.simId,
        clientId: this.filters.clientId,
        startDate: this.filters.startDate,
        endDate: this.filters.endDate
      })
      if (result.error) {
        this.showSnackbar(result.message, 'error')
      } else {
        this.showSnackbar('Export CSV descarcat', 'success')
      }
    },

    async triggerImport() {
      const today = new Date().toISOString().split('T')[0]
      this.importToDate = today
      this.lastImportDate = null
      this.lastImportTotalRecords = 0

      // Pre-fill from current filters
      if (this.filters.clientId) {
        this.importClientId = this.filters.clientId
        await this.loadImportSims(this.filters.clientId)
        if (this.filters.simId) {
          this.importSimId = this.filters.simId
          // Load last import info for this SIM
          await this.loadLastImportInfo(this.filters.simId)
        }
      } else {
        this.importClientId = null
        this.importSimId = null
        this.importSimsList = []
      }

      // Default fromDate = last import date or today
      this.importFromDate = this.lastImportDate || today
      this.importDialog = true
    },

    async loadLastImportInfo(simId) {
      if (!simId) return
      try {
        const result = await getTrafficLastImport(this.token, { simId })
        if (!result.error) {
          this.lastImportDate = result.last_import_date || null
          this.lastImportTotalRecords = result.total_records || 0
        }
      } catch (e) {
        console.error('Error loading last import info:', e)
      }
    },

    async onImportClientChange(clientId) {
      this.importSimId = null
      this.importSimsList = []
      this.lastImportDate = null
      this.lastImportTotalRecords = 0
      if (!clientId) return
      await this.loadImportSims(clientId)
    },

    async loadImportSims(clientId) {
      this.loadingImportSims = true
      try {
        const { BASE_URL } = await import('@/composables/url_config')
        const axios = (await import('axios')).default
        const headers = {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
        const params = new URLSearchParams()
        params.append('client_id', clientId)
        params.append('include_subclients', 'true')
        params.append('has_subscriber_id', 'true')
        params.append('sim_status_id', 'all')
        params.append('per_page', '500')
        const result = await axios.get(`${BASE_URL}/home/get_sims_list_api?${params}`, { headers })
        if (result.data && !result.data.error) {
          this.importSimsList = result.data.sims || []
        }
      } catch (e) {
        console.error('Error loading import SIMs:', e)
      } finally {
        this.loadingImportSims = false
      }
    },

    importSimLabel(sim) {
      const msisdn = sim.msisdn_number || sim.msisdn || ''
      const serial = sim.serial || sim.imsi_serial || sim.id
      const status = sim.sim_status_name ? ` [${sim.sim_status_name}]` : ''
      return `${msisdn} (${serial})${status}`
    },

    async onImportSimChange(simId) {
      this.lastImportDate = null
      this.lastImportTotalRecords = 0
      if (simId) {
        await this.loadLastImportInfo(simId)
        // Default to import since last import date
        if (this.lastImportDate) {
          this.importFromDate = this.lastImportDate
        }
      }
    },

    setImportPeriod(period) {
      const today = new Date()
      const fmt = (d) => d.toISOString().split('T')[0]
      this.importToDate = fmt(today)

      switch (period) {
        case 'today':
          this.importFromDate = fmt(today)
          break
        case 'yesterday': {
          const d = new Date(today)
          d.setDate(d.getDate() - 1)
          this.importFromDate = fmt(d)
          break
        }
        case 'last7': {
          const d = new Date(today)
          d.setDate(d.getDate() - 7)
          this.importFromDate = fmt(d)
          break
        }
        case 'last30': {
          const d = new Date(today)
          d.setDate(d.getDate() - 30)
          this.importFromDate = fmt(d)
          break
        }
        case 'currentMonth': {
          const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
          this.importFromDate = fmt(firstDay)
          break
        }
        case 'lastMonth': {
          const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1)
          const lastDay = new Date(today.getFullYear(), today.getMonth(), 0)
          this.importFromDate = fmt(firstDay)
          this.importToDate = fmt(lastDay)
          break
        }
        case 'sinceLastImport':
          if (this.lastImportDate) {
            this.importFromDate = this.lastImportDate
          }
          break
      }
    },

    async executeImport() {
      this.importing = true
      try {
        const result = await importTrafficData(this.token, {
          simId: this.importSimId,
          fromDate: this.importFromDate,
          toDate: this.importToDate
        })
        if (result.error) {
          this.showSnackbar(result.message, 'error')
        } else {
          this.showSnackbar(`Import realizat: ${result.imported_count || 0} inregistrari`, 'success')
          this.importDialog = false
          await this.loadData()
        }
      } finally {
        this.importing = false
      }
    },

    showSnackbar(message, color = 'success') {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.snackbar = true
    }
  }
}
</script>
